import React, { Fragment, memo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAnalytics,
  faCog,
  faCommentAltEdit,
  faDotCircle,
  faMapMarkerAlt,
  faUsers,
  faUserTie,
  faComments,
  faUserCircle,
  faArrowFromLeft,
  faPollH,
  faGifts,
} from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import { Dialog, Transition } from '@headlessui/react'
import { MenuIcon, XIcon } from '@heroicons/react/outline'
import { Link, NavLink } from 'react-router-dom'
import LOGO from '../assets/logo.png'
import { useUser } from '../hooks/useUser'
import authService from '../services/authService'
import DeleteButton from '../components/DeleteButton'

const navigationAdmin = [
  { name: 'Experiences', href: '/experiences', icon: faCommentAltEdit, show: true },
  { name: 'Locations', href: '/locations', icon: faMapMarkerAlt, show: true },
  { name: 'Customers', href: '/customers', icon: faUserTie, show: true },
  { name: 'Offers & Rewards', href: '/rewards-and-offers', icon: faGifts, show: true },
  {
    name: 'Push Notifications',
    href: 'https://console.firebase.google.com/u/0/project/buzzl-live/notification',
    icon: faDotCircle,
    show: true,
    external: true,
  },
  { name: 'Analytics', href: '/analytics', icon: faAnalytics, show: true },
  { name: 'App Users', href: '/app-users', icon: faUsers, show: true },
  { name: 'Buzzjar Users', href: '/buzzl-users', icon: faCog, show: true },
]

const DashboardLayout: React.FC = (props) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const user = useUser()

  const navigation =
    !user.isLoading && !user.data
      ? []
      : user.data?.type === 'CUSTOMER'
      ? [
          { name: 'Experiences', href: '/experiences', icon: faCommentAltEdit, show: true },
          { name: 'Locations', href: '/locations', icon: faMapMarkerAlt, show: true },
          { name: 'Offers & Rewards', href: '/rewards-and-offers', icon: faGifts, show: true },
          { name: 'Analytics', href: '/analytics', icon: faAnalytics, show: user.data?.role !== 'OPERATOR' },
          { name: 'Users', href: '/users', icon: faUsers, show: user.data?.role !== 'OPERATOR' },
          { name: 'Chat', href: '/chat', icon: faComments, show: true },
          { name: 'Brand details', href: '/brand-details', icon: faPollH, show: user.data?.role === 'SUPER_ADMIN' },
        ]
      : navigationAdmin

  return (
    <>
      <div className="h-full bg-gray-100">
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="fixed inset-0 z-40 flex md:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
                  <Link className="flex flex-shrink-0 items-center px-4" to="/experiences">
                    <img className="h-10 w-auto" src={LOGO} alt="Logo" />
                  </Link>
                  <nav className="mt-5 space-y-1 px-2">
                    {navigation
                      .filter((item) => item.show)
                      .map((item) => {
                        if (item.external) {
                          return (
                            <a
                              key={item.name}
                              href={item.href}
                              target="_blank"
                              rel="noreferrer"
                              className="group flex items-center rounded-md px-2 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                            >
                              <div className="w-8">
                                <FontAwesomeIcon
                                  size="lg"
                                  className={clsx(
                                    false ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
                                    'mr-3 h-6 w-6 flex-shrink-0'
                                  )}
                                  icon={item.icon}
                                />
                              </div>
                              {item.name}
                            </a>
                          )
                        }

                        return (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            className={({ isActive }) =>
                              clsx(
                                isActive
                                  ? 'bg-gray-100 text-gray-900'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                                'group flex items-center rounded-md px-2 py-2 text-base font-medium'
                              )
                            }
                          >
                            <div className="w-8">
                              <FontAwesomeIcon
                                size="lg"
                                className={clsx(
                                  false ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
                                  'mr-3 h-6 w-6 flex-shrink-0'
                                )}
                                icon={item.icon}
                              />
                            </div>
                            {item.name}
                          </NavLink>
                        )
                      })}
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="w-14 flex-shrink-0">{/* Force sidebar to shrink to fit close icon */}</div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white">
            <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
              <Link className="flex flex-shrink-0 items-center px-4" to="/experiences">
                <img className="h-10 w-auto" src={LOGO} alt="Logo" />
              </Link>
              <nav className="mt-5 flex-1 space-y-1 bg-white px-2">
                {navigation
                  .filter((item) => item.show)
                  .map((item) => {
                    if (item.external) {
                      return (
                        <a
                          key={item.name}
                          href={item.href}
                          target="_blank"
                          rel="noreferrer"
                          className="group flex items-center rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        >
                          <div className="w-8">
                            <FontAwesomeIcon
                              size="lg"
                              className={clsx(
                                false ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
                                'mr-3 flex-shrink-0'
                              )}
                              icon={item.icon}
                            />
                          </div>
                          {item.name}
                        </a>
                      )
                    }

                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        className={({ isActive }) =>
                          clsx(
                            isActive
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                            'group flex items-center rounded-md px-2 py-2 text-sm font-medium'
                          )
                        }
                      >
                        <div className="w-8">
                          <FontAwesomeIcon
                            size="lg"
                            className={clsx(
                              false ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
                              'mr-3 flex-shrink-0'
                            )}
                            icon={item.icon}
                          />
                        </div>
                        {item.name}
                      </NavLink>
                    )
                  })}
              </nav>
            </div>
            <div className="flex flex-shrink-0 border-t border-gray-200 p-4">
              <div className="group block w-full flex-shrink-0">
                <div className="flex items-center">
                  <div className="flex">
                    <FontAwesomeIcon size="lg" className="h-9 w-9 text-gray-400" icon={faUserCircle} />
                  </div>
                  <p className="ml-3 overflow-hidden text-ellipsis text-sm font-medium text-gray-600">
                    {user.data?.email}
                  </p>
                  <div className="ml-3 flex">
                    <DeleteButton modalTitle="Are you sure you want to log out?" onClick={authService.logout} alert>
                      <FontAwesomeIcon size="lg" className="h-9 w-9 text-red-400" icon={faArrowFromLeft} />
                    </DeleteButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex min-h-screen flex-1 flex-col bg-gray-50 md:pl-64">
          <div className="sticky top-0 z-10 bg-gray-100 pl-1 pt-1 sm:pl-3 sm:pt-3 md:hidden">
            <button
              type="button"
              className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <MenuIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <main className="flex-1">
            <div className="relative mx-auto mt-10 max-w-7xl px-4 sm:px-6 md:px-8">{props.children}</div>
          </main>
        </div>
      </div>
    </>
  )
}
export default memo(DashboardLayout)
